import { conditionalRender } from '@repo/toolkit/conditional-render'
import DateStringInput from '@repo/ui/date-string-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getAllOtoIndividualPlaces } from '../../../api/journey-place/get-all-oto-individual-places'
import { getJourneyPlaces } from '../../../api/journey-place/get-journey-places'
import useTranslate from '../../../lib/hooks/use-translate'
import { OtoIndividualPlacementFilters } from '../../../lib/types/models/enrollment/oto-individual-enrollments'
import {
  getOtoIndividualWeeksLabel,
  OTO_INDIVIDUAL_NUMBER_OF_HOURS,
  OTO_INDIVIDUAL_NUMBER_OF_WEEKS
} from '../enrollments/utils/oto-individual-utils'

type Props = {
  filter?: OtoIndividualPlacementFilters
  onFilterChange: (filter: OtoIndividualPlacementFilters) => void
}

// The régions du séjour are the OTO Individual lieux de séjour; the lieux d'arrivée come from
// their own list endpoint, which narrows them to the selected région.
const OtoIndividualPlacementFilter = ({ filter, onFilterChange }: Props) => {
  const { t } = useTranslate()

  function setFilter(value: OtoIndividualPlacementFilters) {
    onFilterChange({ ...filter, ...value })
  }

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journey-places', 'FULL', { isOtoIndividual: true }],
    queryFn: () => getJourneyPlaces({ version: 'FULL', filter: { isOtoIndividual: true } })
  })

  const selectedJourneyPlaceId = filter?.journeyPlaceId

  // the backend narrows the lieux d'arrivée to the selected région; with none selected they all
  // come back
  const { data: otoIndividualPlaces } = useQuery({
    queryKey: ['oto-individual-places', { journeyPlaceId: selectedJourneyPlaceId }],
    queryFn: () =>
      getAllOtoIndividualPlaces({
        filter: { ...(selectedJourneyPlaceId && { journeyPlaceId: selectedJourneyPlaceId }) }
      })
  })

  const regions = journeyPlaces?.data ?? []
  const selectedRegion = regions.find((region) => region.id === selectedJourneyPlaceId)

  const arrivalPlaces = otoIndividualPlaces?.data ?? []

  const selectedArrivalPlace = arrivalPlaces.find((place) => place.id === filter?.arrivalPlaceId)

  return (
    <div className="flex flex-col gap-3">
      <div />

      <SelectInput<string>
        searchable
        label={t('oto.individual.placements.filter.country')}
        options={countries?.data.map((country) => ({
          label: country.name,
          value: country.code
        }))}
        selectedOption={conditionalRender(!!filter?.country, {
          label: countries?.data.find((country) => country.code === filter?.country)?.name ?? '',
          value: filter?.country ?? ''
        })}
        onChange={(country) => setFilter({ country })}
      />

      <SelectInput<number>
        searchable
        label={t('oto.individual.placements.column.area.of.stay')}
        options={regions.map((region) => ({ label: region.name, value: region.id }))}
        selectedOption={conditionalRender(!!selectedJourneyPlaceId, {
          label: selectedRegion?.name ?? '',
          value: selectedJourneyPlaceId as number
        })}
        onChange={(journeyPlaceId) => setFilter({ journeyPlaceId, arrivalPlaceId: undefined })}
      />

      <SelectInput<number>
        searchable
        label={t('oto.individual.placements.column.arrival.location')}
        options={arrivalPlaces.map((place) => ({ label: place.title, value: place.id }))}
        selectedOption={conditionalRender(!!filter?.arrivalPlaceId, {
          label: selectedArrivalPlace?.title ?? '',
          value: filter?.arrivalPlaceId as number
        })}
        onChange={(arrivalPlaceId) => setFilter({ arrivalPlaceId })}
      />

      <SelectInput<number>
        label={t('oto.individual.placements.column.hours.per.week')}
        options={OTO_INDIVIDUAL_NUMBER_OF_HOURS.map((hours) => ({
          label: `${hours}h`,
          value: hours
        }))}
        selectedOption={conditionalRender(!!filter?.numberOfHours, {
          label: `${filter?.numberOfHours}h`,
          value: filter?.numberOfHours as number
        })}
        onChange={(numberOfHours) => setFilter({ numberOfHours })}
      />

      <SelectInput<number>
        label={t('oto.individual.placements.column.weeks')}
        options={OTO_INDIVIDUAL_NUMBER_OF_WEEKS.map((weeks) => ({
          label: getOtoIndividualWeeksLabel(weeks),
          value: weeks
        }))}
        selectedOption={conditionalRender(!!filter?.numberOfWeeks, {
          label: getOtoIndividualWeeksLabel(filter?.numberOfWeeks as number),
          value: filter?.numberOfWeeks as number
        })}
        onChange={(numberOfWeeks) => setFilter({ numberOfWeeks })}
      />

      <div className="grid grid-cols-2 gap-2 mobile:grid-cols-1 tablet:grid-cols-2">
        <DateStringInput
          mode="over"
          label={t('oto.individual.placements.filter.departure.date')}
          value={filter?.departureDate}
          onChange={(date) => setFilter({ departureDate: date ?? undefined })}
        />
        <DateStringInput
          mode="over"
          label={t('oto.individual.placements.filter.arrival.date')}
          value={filter?.returnDate}
          onChange={(date) => setFilter({ returnDate: date ?? undefined })}
        />
      </div>
    </div>
  )
}

export default OtoIndividualPlacementFilter
